import React, { useState, useEffect } from "react";
import { apiGet, apiPut, apiDelete } from "../utils/api";
import Layout from "./Layout";

function EntryTable() {
  const [entries, setEntries] = useState([]);
  const [categories, setCategories] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [filterCategory, setFilterCategory] = useState("all");
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({
    date: "",
    category: "",
    value: "",
    currency: "",
    notes: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = () => {
    apiGet("/wealth_entries")
      .then((res) => res.json())
      .then(setEntries)
      .catch((err) => console.error("Fehler beim Laden:", err));

    apiGet("/categories")
      .then((res) => res.json())
      .then(setCategories)
      .catch((err) => console.error("Fehler beim Laden der Kategorien:", err));

    apiGet("/currencies")
      .then((res) => res.json())
      .then(setCurrencies)
      .catch((err) => console.error("Fehler beim Laden der Währungen:", err));
  };

  const handleDelete = (id) => {
    if (!window.confirm("Eintrag wirklich löschen?")) return;
    apiDelete(`/wealth_entries/${id}`)
      .then(() => setEntries((prev) => prev.filter((entry) => entry.id !== id)))
      .catch((err) => console.error("Fehler beim Löschen:", err));
  };

  const handleEditClick = (entry) => {
    setEditingId(entry.id);
    setEditForm({
      date: entry.date,
      category: entry.category,
      value: entry.value,
      currency: entry.currency,
      notes: entry.notes || "",
    });
    // Prevent body scroll on mobile when modal opens
    if (window.innerWidth < 768) {
      document.body.style.overflow = 'hidden';
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditForm({
      date: "",
      category: "",
      value: "",
      currency: "",
      notes: "",
    });
    // Re-enable body scroll
    document.body.style.overflow = '';
  };

  const handleSave = (e) => {
    e.preventDefault();
    apiPut(`/wealth_entries/${editingId}`, editForm)
      .then((res) => res.json())
      .then((updated) => {
        setEntries((prev) =>
          prev.map((e) => (e.id === updated.id ? updated : e))
        );
        handleCancelEdit();
      })
      .catch((err) => console.error("Fehler beim Speichern:", err));
  };

  // Cleanup effect for body scroll
  useEffect(() => {
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Filter entries based on selected category
  const filteredEntries = filterCategory === "all"
    ? entries
    : entries.filter(entry => entry.category === filterCategory);

  return (
    <Layout title="Alle Einträge">
      {/* Filter Section */}
      <div className="card" style={{ marginBottom: 'var(--space-4)' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-4)',
          flexWrap: 'wrap'
        }}>
          <div style={{ flex: '0 0 auto' }}>
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{ verticalAlign: 'middle', marginRight: 'var(--space-2)' }}
            >
              <path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span style={{
              fontSize: 'var(--font-size-md)',
              fontWeight: '600',
              color: 'var(--text-primary)'
            }}>
              Kategorie filtern:
            </span>
          </div>
          <div style={{ flex: '1 1 250px', minWidth: '200px' }}>
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              style={{
                width: '100%',
                fontSize: 'var(--font-size-md)',
                padding: 'var(--space-2) var(--space-3)',
              }}
            >
              <option value="all">Alle Kategorien ({entries.length})</option>
              {categories.map((cat) => {
                const count = entries.filter(e => e.category === cat.name).length;
                return (
                  <option key={cat.id} value={cat.name}>
                    {cat.name} ({count})
                  </option>
                );
              })}
            </select>
          </div>
          {filterCategory !== "all" && (
            <button
              onClick={() => setFilterCategory("all")}
              className="btn btn-sm btn-secondary"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-2)'
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              Filter zurücksetzen
            </button>
          )}
        </div>
        {filterCategory !== "all" && (
          <div style={{
            marginTop: 'var(--space-3)',
            padding: 'var(--space-3)',
            background: 'var(--bg-secondary)',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-sm)',
            color: 'var(--text-secondary)'
          }}>
            Zeige <strong>{filteredEntries.length}</strong> von <strong>{entries.length}</strong> Einträgen
          </div>
        )}
      </div>


      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>Datum</th>
              <th>Kategorie</th>
              <th>Wert</th>
              <th>Währung</th>
              <th>Notizen</th>
              <th style={{ width: '100px' }}></th>
            </tr>
          </thead>
          <tbody>
            {filteredEntries.length === 0 ? (
              <tr>
                <td colSpan={6} className="no-entries">
                  {entries.length === 0
                    ? "Keine Einträge vorhanden."
                    : "Keine Einträge für die ausgewählte Kategorie."}
                </td>
              </tr>
            ) : (
              filteredEntries.map((entry) => (
                <React.Fragment key={entry.id}>
                  {editingId === entry.id ? (
                    <>
                      {/* Mobile: Full-Screen Modal */}
                      <tr className="md:hidden">
                        <td colSpan={6} style={{ padding: 0 }}>
                          <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 z-50 flex items-start justify-center overflow-y-auto p-4">
                            <div className="bg-white dark:bg-secondary-800 rounded-lg shadow-xl w-full max-w-lg my-8">
                              <div className="sticky top-0 bg-secondary-700 dark:bg-secondary-800 text-white px-6 py-4 rounded-t-lg flex items-center justify-between border-b border-secondary-600 dark:border-secondary-700">
                                <h3 className="text-lg font-semibold">Eintrag bearbeiten</h3>
                                <button
                                  type="button"
                                  onClick={handleCancelEdit}
                                  className="text-white hover:bg-secondary-600 dark:hover:bg-secondary-700 rounded-full p-2 transition-colors"
                                >
                                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                    <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                  </svg>
                                </button>
                              </div>
                              <form onSubmit={handleSave} className="p-6">
                                <div className="space-y-4">
                                  <div className="form-group">
                                    <label className="label">Datum</label>
                                    <input
                                      type="date"
                                      value={editForm.date}
                                      onChange={(e) => setEditForm({ ...editForm, date: e.target.value })}
                                      required
                                      className="input"
                                    />
                                  </div>
                                  <div className="form-group">
                                    <label className="label">Kategorie</label>
                                    <select
                                      value={editForm.category}
                                      onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                                      required
                                      className="input"
                                    >
                                      <option value="">Wählen...</option>
                                      {categories.map((cat) => (
                                        <option key={cat.id} value={cat.name}>
                                          {cat.name}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                  <div className="form-group">
                                    <label className="label">Wert</label>
                                    <input
                                      type="number"
                                      step="0.01"
                                      value={editForm.value}
                                      onChange={(e) => setEditForm({ ...editForm, value: e.target.value })}
                                      required
                                      className="input"
                                      placeholder="0.00"
                                    />
                                  </div>
                                  <div className="form-group">
                                    <label className="label">Währung</label>
                                    <select
                                      value={editForm.currency}
                                      onChange={(e) => setEditForm({ ...editForm, currency: e.target.value })}
                                      required
                                      className="input"
                                    >
                                      <option value="">Wählen...</option>
                                      {currencies.map((cur) => (
                                        <option key={cur.id} value={cur.code}>
                                          {cur.code}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                  <div className="form-group">
                                    <label className="label">Notizen (optional)</label>
                                    <textarea
                                      value={editForm.notes || ""}
                                      onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                                      rows="3"
                                      className="input"
                                      placeholder="Notizen..."
                                    />
                                  </div>
                                </div>
                                <div className="flex gap-3 mt-6">
                                  <button type="submit" className="btn btn-primary flex-1">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="inline mr-2">
                                      <polyline points="20 6 9 17 4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                    Speichern
                                  </button>
                                  <button type="button" className="btn btn-secondary flex-1" onClick={handleCancelEdit}>
                                    Abbrechen
                                  </button>
                                </div>
                                <div className="mt-4 pt-4 border-t border-secondary-200 dark:border-secondary-700">
                                  <button
                                    type="button"
                                    className="btn btn-secondary w-full text-secondary-600 dark:text-secondary-400 hover:text-secondary-800 dark:hover:text-secondary-200"
                                    onClick={() => {
                                      if (window.confirm("Eintrag wirklich löschen?")) {
                                        handleDelete(entry.id);
                                        handleCancelEdit();
                                      }
                                    }}
                                  >
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="inline mr-2">
                                      <path d="M2 4H3.33333H14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                      <path d="M5.33301 4.00004V2.66671C5.33301 2.31309 5.47348 1.97395 5.72353 1.7239C5.97358 1.47385 6.31272 1.33337 6.66634 1.33337H9.33301C9.68663 1.33337 10.0258 1.47385 10.2758 1.7239C10.5259 1.97395 10.6663 2.31309 10.6663 2.66671V4.00004M12.6663 4.00004V13.3334C12.6663 13.687 12.5259 14.0261 12.2758 14.2762C12.0258 14.5262 11.6866 14.6667 11.333 14.6667H4.66634C4.31272 14.6667 3.97358 14.5262 3.72353 14.2762C3.47348 14.0261 3.33301 13.687 3.33301 13.3334V4.00004H12.6663Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                    Eintrag löschen
                                  </button>
                                </div>
                              </form>
                            </div>
                          </div>
                        </td>
                      </tr>

                      {/* Desktop/Tablet: Inline Edit */}
                      <tr className="hidden md:table-row inline-edit-row">
                        <td colSpan={6} style={{ padding: 0 }}>
                          <div className="bg-secondary-50 dark:bg-secondary-800 border border-secondary-200 dark:border-secondary-700 p-4 rounded-lg m-2 shadow-sm">
                            <form onSubmit={handleSave}>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div className="form-group">
                                  <label className="label text-xs mb-1 font-semibold">Datum</label>
                                  <input
                                    type="date"
                                    value={editForm.date}
                                    onChange={(e) => setEditForm({ ...editForm, date: e.target.value })}
                                    required
                                    className="input text-sm p-2"
                                  />
                                </div>
                                <div className="form-group">
                                  <label className="label text-xs mb-1 font-semibold">Kategorie</label>
                                  <select
                                    value={editForm.category}
                                    onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                                    required
                                    className="input text-sm p-2"
                                  >
                                    <option value="">Wählen...</option>
                                    {categories.map((cat) => (
                                      <option key={cat.id} value={cat.name}>
                                        {cat.name}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                                <div className="form-group">
                                  <label className="label text-xs mb-1 font-semibold">Wert</label>
                                  <input
                                    type="number"
                                    step="0.01"
                                    value={editForm.value}
                                    onChange={(e) => setEditForm({ ...editForm, value: e.target.value })}
                                    required
                                    className="input text-sm p-2"
                                    placeholder="0.00"
                                  />
                                </div>
                                <div className="form-group">
                                  <label className="label text-xs mb-1 font-semibold">Währung</label>
                                  <select
                                    value={editForm.currency}
                                    onChange={(e) => setEditForm({ ...editForm, currency: e.target.value })}
                                    required
                                    className="input text-sm p-2"
                                  >
                                    <option value="">Wählen...</option>
                                    {currencies.map((cur) => (
                                      <option key={cur.id} value={cur.code}>
                                        {cur.code}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                                <div className="form-group md:col-span-2">
                                  <label className="label text-xs mb-1 font-semibold">Notizen (optional)</label>
                                  <textarea
                                    value={editForm.notes || ""}
                                    onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                                    rows="2"
                                    className="input text-sm p-2 resize-y min-h-[50px]"
                                    placeholder="Notizen..."
                                  />
                                </div>
                              </div>
                              <div className="flex gap-2 mt-3">
                                <button type="submit" className="btn btn-primary flex-1 text-sm">
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="inline mr-1">
                                    <polyline points="20 6 9 17 4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                  </svg>
                                  Speichern
                                </button>
                                <button type="button" className="btn btn-secondary flex-1 text-sm" onClick={handleCancelEdit}>
                                  Abbrechen
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-secondary text-sm text-secondary-600 dark:text-secondary-400 hover:text-secondary-800 dark:hover:text-secondary-200"
                                  title="Eintrag löschen"
                                  onClick={() => {
                                    if (window.confirm("Eintrag wirklich löschen?")) {
                                      handleDelete(entry.id);
                                      handleCancelEdit();
                                    }
                                  }}
                                >
                                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" className="inline">
                                    <path d="M2 4H3.33333H14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M5.33301 4.00004V2.66671C5.33301 2.31309 5.47348 1.97395 5.72353 1.7239C5.97358 1.47385 6.31272 1.33337 6.66634 1.33337H9.33301C9.68663 1.33337 10.0258 1.47385 10.2758 1.7239C10.5259 1.97395 10.6663 2.31309 10.6663 2.66671V4.00004M12.6663 4.00004V13.3334C12.6663 13.687 12.5259 14.0261 12.2758 14.2762C12.0258 14.5262 11.6866 14.6667 11.333 14.6667H4.66634C4.31272 14.6667 3.97358 14.5262 3.72353 14.2762C3.47348 14.0261 3.33301 13.687 3.33301 13.3334V4.00004H12.6663Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                  </svg>
                                </button>
                              </div>
                            </form>
                          </div>
                        </td>
                      </tr>
                    </>
                  ) : (
                    <tr
                      className="table-row-actions table-row-clickable"
                      onClick={(e) => {
                        // Only trigger on mobile (screen width < 768px) and not when clicking delete button
                        if (window.innerWidth < 768 && !e.target.closest('.btn-icon-danger')) {
                          handleEditClick(entry);
                        }
                      }}
                      style={{ cursor: window.innerWidth < 768 ? 'pointer' : 'default' }}
                    >
                      <td>{entry.date}</td>
                      <td>{entry.category}</td>
                      <td>{Number(entry.value).toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                      <td>{entry.currency}</td>
                      <td className="mobile-hide">{entry.notes}</td>
                      <td className="table-actions">
                        <button
                          className="btn-icon btn-icon-primary desktop-only"
                          onClick={() => handleEditClick(entry)}
                          title="Bearbeiten"
                        >
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M11.333 2.00004C11.5081 1.82494 11.716 1.68605 11.9447 1.59129C12.1735 1.49653 12.4187 1.44775 12.6663 1.44775C12.914 1.44775 13.1592 1.49653 13.3879 1.59129C13.6167 1.68605 13.8246 1.82494 13.9997 2.00004C14.1748 2.17513 14.3137 2.383 14.4084 2.61178C14.5032 2.84055 14.552 3.08575 14.552 3.33337C14.552 3.58099 14.5032 3.82619 14.4084 4.05497C14.3137 4.28374 14.1748 4.49161 13.9997 4.66671L4.99967 13.6667L1.33301 14.6667L2.33301 11L11.333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>
                        <button
                          className="btn-icon btn-icon-danger"
                          onClick={(e) => {
                            e.stopPropagation(); // Prevent row click on mobile
                            handleDelete(entry.id);
                          }}
                          title="Löschen"
                        >
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M2 4H3.33333H14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M5.33301 4.00004V2.66671C5.33301 2.31309 5.47348 1.97395 5.72353 1.7239C5.97358 1.47385 6.31272 1.33337 6.66634 1.33337H9.33301C9.68663 1.33337 10.0258 1.47385 10.2758 1.7239C10.5259 1.97395 10.6663 2.31309 10.6663 2.66671V4.00004M12.6663 4.00004V13.3334C12.6663 13.687 12.5259 14.0261 12.2758 14.2762C12.0258 14.5262 11.6866 14.6667 11.333 14.6667H4.66634C4.31272 14.6667 3.97358 14.5262 3.72353 14.2762C3.47348 14.0261 3.33301 13.687 3.33301 13.3334V4.00004H12.6663Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>
    </Layout>
  );
}

export default EntryTable;
